import React, { useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useInventory } from 'components/hooks';
import { HeadlinePage, FormMonacoEditor, FormChipList } from 'components/common';
import { useTranslation } from 'react-i18next';
import {  inventoryPolicies } from 'helpers';
import { useStyle } from './styles';
import { Input } from 'components/layout';
import InventoryHost from './InventoryHost/InventoryHost';
import {
  Paper,
  Grid,
  CircularProgress,
  Backdrop,
  Button,
  Typography
} from '@mui/material';
import { ROUTES } from 'consts';
import { InventoryGroup } from './InventoryGroup/InventoryGroup';
import InventoryGroupChildren from "./InventoryChildren/InventoryGroupChildren";


export default function InventoryFormDumb({
  defaultValues,
  handleOnSubmit,
  isEdit,
  companies
}) {
  const { isLoaded } = useInventory();
  const [t] = useTranslation();
  const classes = useStyle();
  const methods = useForm({
    defaultValues: useMemo(() => {
      return { ...defaultValues, company: companies && companies[0].value };
    }, [defaultValues])
  });
  const { handleSubmit, reset, control, setValue, getValues } = methods;
  const [languageSelected, setLanguageSelected] = useState(defaultValues.fileType);
  const [ selectedType , setSelectedType] = useState('Form');
  const [ soloVer , setSoloVer] = useState(document.location.href.indexOf("acc=v") != -1);
  
  const types = [
    {value: 'Form', label: 'Form'},
    {value: 'File', label: 'File'}
  ]

  const fields = [
    { policy: inventoryPolicies.name },
    { policy: inventoryPolicies.description },
    { policy: inventoryPolicies.company, options: companies },
  ];

  useEffect(() => {
    if (isEdit) {
      reset(defaultValues);
      setSelectedType(defaultValues.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  const handleOnChange = (name) => {
    return (value) => setValue(name, value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };



  const handleOnChangeType = (event) => {
    setValue('additionalVariables', '');
    const language = 'ini';//(event.target.value === 'Form') ? 'ini' : 'json';
    setLanguageSelected(language);
    setValue('fileType', language);
    setSelectedType(event.target.value);
  }

  const changeLanguage = (language) => {
    setLanguageSelected(language);
    setValue('fileType', language);
  }

  return (
    <>
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoaded.post || isLoaded.put}
      >
        <CircularProgress color="primary" />
      </Backdrop>

      <HeadlinePage title={ soloVer ? '': (isEdit ? 'inventoryEdit.headline' : 'inventoryCreate.headline')} goBack={ROUTES.INVENTORY_LIST}/>
      <FormProvider { ...methods }>
        <Paper className={classes.paperStyle}>
          <form>
            <Typography variant="h6" sx={{ padding: '8px 0px' }}>
              {t('inventoryList.form.title')}
            </Typography>

            <Grid container spacing={2} sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mb: 1
            }}>
              {fields.map((field) => (
                <Grid item xs={12} sm={12} md={6} lg={4} key={field.policy.id}>
                  <Input
                    {...field.policy}
                    control={control}
                    options={field.options}
                    sx={{ width: '100%' }}
                  />
                </Grid>
              ))}

              <Grid item xs={12} sm={12} md={6} lg={4} key={inventoryPolicies.type.id}>
                <Input
                  { ...inventoryPolicies.type }
                  control={ control }
                  options={ types }
                  sx={{ width: '100%' }}
                  onChange = {handleOnChangeType}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4}>
                <FormChipList
                  {...inventoryPolicies.tags}
                  defaultValue = { getValues('tags') }
                  control={control}
                  onChange={handleOnChange(inventoryPolicies.tags.name)}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12}>
                { (selectedType === 'Form') ?
                   (
                    <FormMonacoEditor
                      label={inventoryPolicies.additionalVariables.label}
                      name={inventoryPolicies.additionalVariables.name}
                      helperText={inventoryPolicies.additionalVariables.helperText}
                      methods={methods}
                      language={'ini'}
                      height='200px'
                      acceptedFormats = {['txt','ini']}
                    />
                  ) : (
                    <FormMonacoEditor
                      label={inventoryPolicies.additionalVariables.label}
                      name={inventoryPolicies.additionalVariables.name}
                      helperText={inventoryPolicies.additionalVariables.helperText}
                      methods={methods}
                      language={languageSelected}
                      handleSetLanguage={changeLanguage}
                      languageOptions={['ini','json', 'yaml']}
                      height='200px'
                    />
                  )
                }

              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ display: selectedType !== 'Form' ? 'none' : 'inherit'}}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <InventoryHost
                  methods={methods}
                  policies={inventoryPolicies}
                  items={getValues(inventoryPolicies.hosts.name)}
                  soloVer={soloVer}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12}>
                <InventoryGroup
                  methods={methods}
                  policies={inventoryPolicies}
                  items={getValues(inventoryPolicies.hostGroups.name)}
                  hosts={getValues(inventoryPolicies.hosts.name)}
                  soloVer={soloVer}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12}>
                <InventoryGroupChildren
                  methods={methods}
                  policies={inventoryPolicies}
                  items={getValues(inventoryPolicies.childrenGroups.name)}
                  groups={getValues(inventoryPolicies.hostGroups.name)}
                  soloVer={soloVer}
                />
              </Grid>
            </Grid>
            {!soloVer?
            <Grid container sx={{ mt: 4 }}>
              <Grid item md={2} lg={3} />
              <Grid item xs={12} sm={12} md={8} lg={6}>
                <Button variant="contained" sx={{ width: '100%' }} onClick={handleSubmit(handleOnSubmit)}>
                  {isEdit ? t('inventoryEdit.components.submit') : t('inventoryCreate.components.submit')}
                </Button>
              </Grid>
              <Grid item md={2} lg={3} />
            </Grid>:null}
          </form>
        </Paper>
      </FormProvider>
    </>
  );
}
